// What an Integrity Check asks of a candidate. Every check includes their identity: a US driver's
// licence or State ID, and a foreign passport when they are not a US citizen. A check of scope
// `identity_work` also asks for the work documents their work-auth type accepts.
//
// An entry written before checks had a scope asks for both, so a missing scope reads
// `identity_work` everywhere.
//
// Written as plain CommonJS with no imports so the API server, which runs its files under plain
// node, can require this same file the browser bundle imports.

const IDENTITY = 'identity';
const IDENTITY_WORK = 'identity_work';

const INTEGRITY_CHECK_SCOPES = Object.freeze({ IDENTITY, IDENTITY_WORK });

/**
 * A stored scope as it is read: `identity` stays, anything else (including no scope) is
 * `identity_work`.
 *
 * @param {*} scope
 * @returns {'identity' | 'identity_work'}
 */
function normalizeScope(scope) {
  return scope === IDENTITY ? IDENTITY : IDENTITY_WORK;
}

/**
 * A scope as a request names it. Naming none asks for `identity_work`; any value other than the
 * two codes is refused.
 *
 * @param {*} value
 * @returns {'identity' | 'identity_work' | null} null for a value that is not a scope
 */
function parseScope(value) {
  if (value === undefined) return IDENTITY_WORK;
  return value === IDENTITY || value === IDENTITY_WORK ? value : null;
}

/**
 * Whether a check of scope `held` asks for at least the documents a check of scope `wanted` asks
 * for: `identity_work` covers both, `identity` covers only `identity`.
 *
 * @param {*} held
 * @param {*} wanted
 * @returns {boolean}
 */
function scopeCovers(held, wanted) {
  return normalizeScope(held) === IDENTITY_WORK || normalizeScope(wanted) === IDENTITY;
}

/**
 * The stored scope values of the entries that cover `scope`, for a database filter. `null` stands
 * for an entry with no scope.
 *
 * @param {*} scope
 * @returns {Array<string | null>}
 */
function scopesCovering(scope) {
  return normalizeScope(scope) === IDENTITY ? [IDENTITY, IDENTITY_WORK, null] : [IDENTITY_WORK, null];
}

/**
 * The wider of two scopes.
 *
 * @param {*} a
 * @param {*} b
 * @returns {'identity' | 'identity_work'}
 */
function widerScope(a, b) {
  return normalizeScope(a) === IDENTITY && normalizeScope(b) === IDENTITY ? IDENTITY : IDENTITY_WORK;
}

module.exports = {
  INTEGRITY_CHECK_SCOPES,
  normalizeScope,
  parseScope,
  scopeCovers,
  scopesCovering,
  widerScope,
};
